import { clsx } from "clsx";
import { type FC, useEffect, useRef, useState } from "react";
import { mergeProps, useFocusRing, useHover, useSliderThumb, VisuallyHidden } from "react-aria";
import type { SliderState } from "react-stately";

import { DEPRECATED_useTooltip as useTooltip } from "../../hooks/DEPRECATED_useTooltip.ts";
import { Tooltip } from "../Tooltip/Tooltip.tsx";
import style from "./Slider.module.css";
import type { SliderProps } from "./Slider.tsx";

type ThumbProps = Omit<Parameters<typeof useSliderThumb>[0], "inputRef"> & {
  state: SliderState;
  tooltip?: SliderProps["tooltip"];
};

export const Thumb: FC<ThumbProps> = (props) => {
  const { state, trackRef, index } = props;
  const inputRef = useRef(null);
  const { thumbProps, inputProps } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
    },
    state,
  );
  const { focusProps, isFocusVisible } = useFocusRing();
  const { hoverProps, isHovered } = useHover({ isDisabled: props.isDisabled });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const {
    state: tooltipState,
    tooltipProps,
    triggerProps,
  } = useTooltip(triggerRef, tooltipRef, {
    isDisabled: props.isDisabled || !props.tooltip,
    offset: 10,
    isOpen: (isFocusVisible || isHovered) && !(props.isDisabled || !props.tooltip),
  });

  // Move tooltip according to the thumb cursor
  // TODO: Maybe add a debounce when isHovered = false in case the mouse is faster than the UI.
  const startLeftRef = useRef(0);
  const [tooltipLeft, setTooltipLeft] = useState(0);
  const thumbPercent = state.getThumbPercent(index ?? 0);
  const tooltipStyleLeftValue = tooltipProps.style?.left;
  const tooltipStyleOffset = typeof tooltipStyleLeftValue === "number" ? tooltipStyleLeftValue : 0;

  useEffect(() => {
    const element = trackRef.current;

    if (!element) {
      return;
    }

    const width = element.getBoundingClientRect().width ?? 0;

    if (isHovered || !tooltipState.isOpen) {
      startLeftRef.current = width * thumbPercent;
    }

    setTooltipLeft(-startLeftRef.current + tooltipStyleOffset + width * thumbPercent);
  }, [isHovered, thumbPercent, tooltipStyleOffset, tooltipState.isOpen, trackRef]);

  return (
    <div
      className={clsx(style.thumb_container, { [style.thumb_container_small]: state.values.length > 1 })}
      style={{
        left: `${state.getThumbPercent(index ?? 0) * 100}%`,
        zIndex: isHovered ? 2 : 1,
      }}
      {...hoverProps}
    >
      <div
        {...mergeProps(thumbProps, triggerProps)}
        className={clsx(style.thumb, {
          [style.thumb_disabled]: props.isDisabled,
          [style.thumb_focus]: isFocusVisible,
          [style.thumb_dragging]: state.isThumbDragging(index ?? 0),
        })}
        ref={triggerRef}
      >
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </div>
      {!!tooltipState.isOpen && (
        <Tooltip domProps={{ ...tooltipProps, style: { ...tooltipProps.style, left: tooltipLeft } }} ref={tooltipRef}>
          {typeof props.tooltip === "boolean" && !!props.tooltip && String(state.getThumbValue(index ?? 0))}
          {typeof props.tooltip === "function" && props.tooltip(state.getThumbValue(index ?? 0))!}
        </Tooltip>
      )}
    </div>
  );
};
